import type { SponsoredBrandStore } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { FieldGroup } from "@/components/ui/field";
import { ChoiceRadioCard, ChoiceRadioGroup, SelectField } from "./FormFields";
import type {
  AdFormat,
  LandingPage,
  ProductCollectionLandingPage,
  VideoLandingPage,
} from "./sponsored-brands-model";

interface LandingPageFieldsProps {
  adFormat: AdFormat;
  landingPage: LandingPage;
  stores: SponsoredBrandStore[];
  selectedStore: SponsoredBrandStore | null;
  storeEntityId: string | null;
  storePageUrl: string | null;
  storesLoading: boolean;
  onStoreChange: (value: string | null) => void;
  onStorePageChange: (value: string | null) => void;
  onSpotlightStoreChange: (value: string | null) => void;
  onVideoLandingPageChange: (value: VideoLandingPage) => void;
  onProductCollectionLandingPageChange: (value: ProductCollectionLandingPage) => void;
}

export function LandingPageFields({
  adFormat,
  landingPage,
  stores,
  selectedStore,
  storeEntityId,
  storePageUrl,
  storesLoading,
  onStoreChange,
  onStorePageChange,
  onSpotlightStoreChange,
  onVideoLandingPageChange,
  onProductCollectionLandingPageChange,
}: LandingPageFieldsProps) {
  const { t } = useTranslation("campaignCreation");
  const storeOptions = stores.map((store) => ({ value: store.entityId, label: store.name }));
  const storeFields = (
    <div className="grid grid-cols-2 gap-4 pt-3">
      <SelectField
        label={t("sb.landing.chooseStore")}
        value={storeEntityId}
        onValueChange={onStoreChange}
        options={storeOptions}
        disabled={storesLoading || stores.length === 0}
        description={stores.length === 0 ? t("sb.blockers.storeData") : undefined}
      />
      <SelectField
        label={t("sb.landing.choosePage")}
        value={storePageUrl}
        onValueChange={onStorePageChange}
        options={(selectedStore?.pages ?? []).map((page) => ({
          value: page.url,
          label: page.name,
        }))}
        disabled={!selectedStore}
      />
    </div>
  );

  if (adFormat === "storeSpotlight") {
    return (
      <FieldGroup>
        <p className="text-sm text-muted-foreground">{t("sb.landing.storeRequirement")}</p>
        <SelectField
          label={t("sb.landing.chooseStore")}
          value={storeEntityId}
          onValueChange={onSpotlightStoreChange}
          options={storeOptions}
          disabled={storesLoading || stores.length === 0}
          description={stores.length === 0 ? t("sb.blockers.storeSpotlightData") : undefined}
          required
        />
      </FieldGroup>
    );
  }
  if (adFormat === "video") {
    return (
      <ChoiceRadioGroup
        value={landingPage}
        onValueChange={(value) => onVideoLandingPageChange(value as VideoLandingPage)}
      >
        <ChoiceRadioCard value="amazonStore" label={t("sb.landing.amazonStore")}>
          {storeFields}
        </ChoiceRadioCard>
        <ChoiceRadioCard value="productDetailPage" label={t("sb.landing.productDetailPage")} />
      </ChoiceRadioGroup>
    );
  }
  return (
    <ChoiceRadioGroup
      value={landingPage}
      onValueChange={(value) =>
        onProductCollectionLandingPageChange(value as ProductCollectionLandingPage)
      }
    >
      <ChoiceRadioCard value="amazonStore" label={t("sb.landing.amazonStore")}>
        {storeFields}
      </ChoiceRadioCard>
      <ChoiceRadioCard
        value="newLandingPage"
        label={t("sb.landing.newLandingPage")}
        description={t("sb.landing.newLandingDescription")}
      />
    </ChoiceRadioGroup>
  );
}
